import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { TextareaInputComponent } from '../../../../../shared/components/textarea-input/textarea-input.component';
import { CustomButtonComponent } from "../../../../../shared/components/custom-button/custom-button.component";
import { FormulaInputBoxComponent } from "../formula-input-box/formula-input-box.component";
import { IndicatorGet } from '../../../../../core/interfaces/sales-engine-interfaces';
import { CommonValidatorService } from '../../../../../core/services/validators/common-validator.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { firstValueFrom } from 'rxjs';
import { SalesEngineService } from '../../../../../core/api/sales-engine.service';

@Component({
  selector: 'app-create-edit-indicator',
  standalone: true,
  imports: [TranslocoPipe, CommonModule, LabeledInputComponent, TextareaInputComponent, CustomButtonComponent, FormulaInputBoxComponent],
  templateUrl: './create-edit-indicator.component.html',
  styleUrl: './create-edit-indicator.component.css'
})
export class CreateEditIndicatorComponent implements OnChanges {
  constructor(private commonValidator: CommonValidatorService, private apiError: ApiErrorService, private salesEngine: SalesEngineService) { }

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  @Input() isEditing: boolean = false;
  @Input() indicator?: IndicatorGet;

  @Input() toast?: ToastComponent;
  @Input() reloadCallbackFunction?: () => void;

  // Inputs
  nameInput: string = '';
  nameInputError: string | null = null;

  unitInput: string = '';
  unitInputError: string | null = null;

  descriptionInput: string = '';
  descriptionInputError: string | null = null;

  formulaInput: string = '';

  // Button click
  confirmClicked: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    this.resetData();
    if (this.indicator != undefined) {
      this.populateInputsFromIndicator(this.indicator)
    }
  }

  closeView() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }

  populateInputsFromIndicator(indicator: IndicatorGet) {
    this.nameInput = indicator.name || '';
    this.unitInput = indicator.unit || '';
    this.descriptionInput = indicator.description || '';

    // TODO: EVuk - implement logic for json
    this.formulaInput = indicator.formula;
  }

  resetData() {
    this.nameInput = this.unitInput = this.descriptionInput = '';
    this.nameInputError = this.unitInputError = this.descriptionInputError = null;
    this.formulaInput = '';
  }

  validateInputs(): boolean {
    const nameInputValue = this.nameInput.trim();
    const unitInputValue = this.unitInput.trim();
    const descriptionInputValue = this.descriptionInput.trim();

    // Validate length for text inputs
    const nameError = this.commonValidator.validateLength(nameInputValue, 2, 255);
    const unitError = this.commonValidator.validateLength(unitInputValue, 2, 255, false);
    const descriptionError = this.commonValidator.validateLength(descriptionInputValue, 2, 255, false);

    // Show errors
    this.nameInputError = nameError;
    this.unitInputError = unitError;
    this.descriptionInputError = descriptionError;

    // If there are any errors show them and prevent submission
    if (
      nameError || unitError || descriptionError) {
      return false;
    }

    return true;
  }

  getIndicatorFromInputs(id: string = ''): IndicatorGet {
    const result: IndicatorGet = {
      id: id,
      name: this.nameInput.trim(),
      formula: this.formulaInput.trim(),
      description: this.descriptionInput.trim(),
      unit: this.unitInput.trim(),
      logic: {} // TODO: EVuk
    }
    return result;
  }

  async confirmButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    // Send request for creating location
    try {
      if (this.indicator) {
        const newData = this.getIndicatorFromInputs(this.indicator.id);
        await firstValueFrom(this.salesEngine.updateIndicator(this.indicator.id, newData));
      }
      else {
        const newData = this.getIndicatorFromInputs();
        await firstValueFrom(this.salesEngine.createIndicator(newData));
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.confirmClicked = false;
      return;
    }

    this.confirmClicked = false;
    this.isOpen = false;

    if (this.reloadCallbackFunction) {
      this.reloadCallbackFunction();
    }
  }
}
